import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, X, Send, Navigation, Bell, Leaf, ShieldCheck, ChevronRight, Bot } from 'lucide-react';
import { aiAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

export default function AgenticAIChatbot() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      sender: 'ai',
      text: `Namaste ${user?.name ? user.name.split(' ')[0] : 'there'}! 🙏 I am **Avartana Care AI**, your personal health & platform companion.\n\nI can help you navigate pages, set reminders, book therapy sessions, or answer any Ayurvedic health questions!`,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const chatEndRef = useRef(null);

  const role = user?.role || 'patient';

  const QUICK_PROMPTS = [
    { label: '🚀 Go to Appointments', text: 'Take me to my appointments page' },
    { label: '👤 My Profile', text: 'Open my profile page' },
    { label: '⏰ Set a Reminder', text: 'Remind me to drink warm herbal tea at 8 PM' },
    { label: '📅 Book Therapy', text: 'Book an appointment for Panchakarma Detox' },
    { label: '🌿 Shirodhara Guide', text: 'What is Shirodhara therapy?' },
    { label: '🛡️ Doctor Licenses', text: 'Tell me about doctor licenses' },
  ];

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isOpen) scrollToBottom();
  }, [messages, isOpen]);

  const handleSend = async (textToSend) => {
    const query = (textToSend || input).trim();
    if (!query || loading) return;

    const userMsg = {
      sender: 'user',
      text: query,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages(prev => [...prev, userMsg]);
    if (!textToSend) setInput('');
    setLoading(true);

    try {
      const res = await aiAPI.chat({ message: query });
      const { reply, action } = res.data;

      const aiMsg = {
        sender: 'ai',
        text: reply || 'How else can I assist you today?',
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        action
      };

      setMessages(prev => [...prev, aiMsg]);

      // Execute Adaptive Action (Navigation or Reminder)
      if (action) {
        if (action.type === 'navigate' && action.path) {
          toast.success(`Navigating to ${action.path}...`);
          setTimeout(() => {
            navigate(action.path);
          }, 800);
        } else if (action.type === 'reminder') {
          toast.success('Reminder saved to Notification Center!');
        }
      }
    } catch (err) {
      setMessages(prev => [
        ...prev,
        {
          sender: 'ai',
          text: 'I am temporarily unable to connect to the network. Please try again.',
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end">
      
      {/* Expanded Chat Window */}
      {isOpen && (
        <div className="mb-4 w-96 max-w-[calc(100vw-2rem)] h-[540px] max-h-[calc(100vh-8rem)] bg-white rounded-3xl shadow-2xl border border-gray-100 flex flex-col overflow-hidden animate-in slide-in-from-bottom-5 duration-300">
          
          {/* Stunning Header */}
          <div className="relative overflow-hidden bg-gradient-to-r from-emerald-700 via-teal-800 to-cyan-900 p-4 text-white flex items-center justify-between shadow-lg">
            <div className="absolute -right-8 -top-8 w-28 h-28 rounded-full bg-white/10 blur-xl pointer-events-none" />
            
            <div className="flex items-center gap-3 relative z-10">
              <div className="w-11 h-11 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/30 text-white shadow-inner flex-shrink-0">
                <Leaf className="w-6 h-6 text-emerald-200 animate-pulse" />
              </div>
              <div>
                <h3 className="font-extrabold text-base leading-tight flex items-center gap-1.5 text-white">
                  Avartana Care AI <Sparkles className="w-4 h-4 text-amber-300 fill-amber-300 animate-bounce" />
                </h3>
                <p className="text-emerald-100 text-xs flex items-center gap-1.5 font-medium mt-0.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" /> Online • Adaptive Health Assistant
                </p>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} className="p-1.5 rounded-xl hover:bg-white/20 text-white/80 hover:text-white transition-colors relative z-10">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages Body */}
          <div className="flex-1 p-4 overflow-y-auto space-y-3.5 bg-gradient-to-b from-gray-50 to-emerald-50/20">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-2xl p-3.5 text-xs sm:text-sm shadow-xs leading-relaxed ${
                  m.sender === 'user'
                    ? 'bg-gradient-to-r from-emerald-600 to-teal-700 text-white rounded-br-none font-medium shadow-md'
                    : 'bg-white border border-gray-200/80 text-gray-800 rounded-bl-none shadow-sm'
                }`}>
                  <div className="whitespace-pre-line">
                    {m.text}
                  </div>
                  {m.action?.type === 'navigate' && (
                    <div className="mt-2.5 pt-2 border-t border-gray-100 flex items-center gap-1.5 text-xs font-semibold text-emerald-700">
                      <Navigation className="w-3.5 h-3.5 text-emerald-600 animate-bounce" /> Auto-Navigating to {m.action.path}...
                    </div>
                  )}
                  {m.action?.type === 'reminder' && (
                    <div className="mt-2.5 pt-2 border-t border-gray-100 flex items-center gap-1.5 text-xs font-semibold text-amber-700">
                      <Bell className="w-3.5 h-3.5 text-amber-600" /> Reminder Saved
                    </div>
                  )}
                  <span className={`block text-[10px] mt-1 text-right ${m.sender === 'user' ? 'text-emerald-200' : 'text-gray-400'}`}>
                    {m.time}
                  </span>
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 rounded-2xl p-3 rounded-bl-none flex items-center gap-2 shadow-xs">
                  <div className="w-2 h-2 rounded-full bg-emerald-500 animate-bounce" />
                  <div className="w-2 h-2 rounded-full bg-teal-500 animate-bounce [animation-delay:0.2s]" />
                  <div className="w-2 h-2 rounded-full bg-cyan-500 animate-bounce [animation-delay:0.4s]" />
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Quick Action Chips */}
          <div className="p-2.5 border-t border-gray-100 bg-white flex gap-1.5 overflow-x-auto no-scrollbar">
            {QUICK_PROMPTS.map((p, idx) => (
              <button key={idx} onClick={() => handleSend(p.text)}
                className="px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200/80 hover:bg-emerald-600 hover:text-white hover:border-emerald-600 flex-shrink-0 transition-all shadow-2xs">
                {p.label}
              </button>
            ))}
          </div>

          {/* Input Box */}
          <form onSubmit={(e) => { e.preventDefault(); handleSend(); }} className="p-3 border-t border-gray-100 bg-white flex items-center gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask Avartana Care AI (e.g., 'Take me to profile' or 'Set a reminder')..."
              className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs sm:text-sm outline-none focus:ring-2 focus:ring-emerald-500 transition-all"
            />
            <button
              type="submit"
              disabled={!input.trim() || loading}
              className="w-10 h-10 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-700 hover:from-emerald-700 hover:to-teal-800 disabled:opacity-40 text-white flex items-center justify-center transition-all shadow-md flex-shrink-0"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>

        </div>
      )}

      {/* Floating Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="group relative flex items-center gap-3 px-5 py-3.5 rounded-full bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-700 text-white font-bold text-sm shadow-xl hover:shadow-2xl hover:scale-105 active:scale-95 transition-all duration-300 border-2 border-white/40"
      >
        <span className="relative flex items-center justify-center">
          <Leaf className="w-5 h-5 group-hover:rotate-12 transition-transform text-emerald-200" />
          <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-amber-400 rounded-full animate-ping" />
        </span>
        <span className="font-extrabold tracking-wide">Avartana Care AI</span>
        <span className="bg-white/20 text-white text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider font-extrabold backdrop-blur-xs">
          AI
        </span>
      </button>

    </div>
  );
}
